// Product Photos (Step 4) — camera + gallery upload, max 3, primary star.
// Extracted from /app/frontend/src/pages/DiamondVerification.jsx.

import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Camera, ImagePlus, Star, X } from 'lucide-react';
import { API, token, MAX_PHOTOS } from './constants';

// Client-side resize: max 1920px wide, JPEG q=0.85 → smaller file, faster upload
const resizeImage = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = (e) => { img.src = e.target.result; };
  img.onerror = reject;
  img.onload = () => {
    const MAX_W = 1920;
    const scale = img.width > MAX_W ? MAX_W / img.width : 1;
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    canvas.getContext('2d').drawImage(img, 0, 0, w, h);
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('Image processing failed')),
      'image/jpeg',
      0.85
    );
  };
  reader.readAsDataURL(file);
});

export const PhotoSection = ({ photos, setPhotos }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const photoSrc = (url) => url.startsWith('http') ? url : `${API}${url}`;

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setError('');
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setError('Image too large (max 15 MB).');
      return;
    }
    if (photos.length >= MAX_PHOTOS) {
      setError(`Maximum ${MAX_PHOTOS} photos.`);
      return;
    }
    setUploading(true);
    try {
      // No-drafts: upload the file to R2 directly, hold the URL in local
      // state. The DV record gets the photo URLs at final atomic submit.
      const blob = await resizeImage(file);
      const fd = new FormData();
      fd.append('file', blob, 'dv-photo.jpg');

      const upRes = await axios.post(`${API}/api/upload`, fd, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      const url = upRes.data.url;
      setPhotos(prev => {
        const next = [...prev, { url, is_primary: prev.length === 0 }];
        return next;
      });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }, [photos.length, setPhotos]);

  const handleRemove = useCallback((idx) => {
    setPhotos(prev => {
      const next = prev.filter((_, i) => i !== idx);
      if (prev[idx]?.is_primary && next.length) next[0] = { ...next[0], is_primary: true };
      return next;
    });
  }, [setPhotos]);

  const handleSetPrimary = useCallback((idx) => {
    setPhotos(prev => prev.map((p, i) => ({ ...p, is_primary: i === idx })));
  }, [setPhotos]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 mb-3 p-4" data-testid="dv-photos-section">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-bold text-gray-800">Product Photos</p>
        <span className="text-[10px] font-semibold uppercase text-gray-500">{photos.length}/{MAX_PHOTOS}</span>
      </div>

      {/* Hidden inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid="dv-photo-camera-input"
        onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="dv-photo-gallery-input"
        onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
      />

      {/* Thumbnails */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {photos.map((p, idx) => (
            <div
              key={`${p.url}-${idx}`}
              className="relative aspect-square rounded-xl overflow-hidden border-2 border-gray-200 bg-gray-50"
              data-testid={`dv-photo-thumb-${idx}`}
            >
              <img src={photoSrc(p.url)} alt={`Product ${idx + 1}`} className="w-full h-full object-cover" />
              <button
                onClick={() => !p.is_primary && handleSetPrimary(idx)}
                className={`absolute top-1 left-1 p-1 rounded-full shadow ${p.is_primary ? 'bg-amber-400' : 'bg-white/80 hover:bg-amber-100'}`}
                data-testid={`dv-photo-primary-${idx}`}
                title={p.is_primary ? 'Primary photo' : 'Set as primary'}
              >
                <Star className={`w-3 h-3 ${p.is_primary ? 'text-white fill-white' : 'text-gray-500'}`} />
              </button>
              <button
                onClick={() => handleRemove(idx)}
                className="absolute top-1 right-1 p-1 rounded-full bg-white/90 hover:bg-red-100 shadow"
                data-testid={`dv-photo-remove-${idx}`}
                title="Remove photo"
              >
                <X className="w-3 h-3 text-red-600" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Action buttons */}
      {photos.length < MAX_PHOTOS && (
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => cameraInputRef.current?.click()}
            disabled={uploading}
            data-testid="dv-photo-take-btn"
            className="py-3 rounded-xl border-2 border-emerald-200 bg-emerald-50 text-emerald-700 font-bold text-sm flex items-center justify-center gap-2 hover:bg-emerald-100 active:bg-emerald-200 disabled:opacity-50"
          >
            <Camera className="w-4 h-4" /> Take Photo
          </button>
          <button
            onClick={() => galleryInputRef.current?.click()}
            disabled={uploading}
            data-testid="dv-photo-choose-btn"
            className="py-3 rounded-xl border-2 border-gray-200 bg-white text-gray-700 font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50"
          >
            <ImagePlus className="w-4 h-4" /> Choose Photo
          </button>
        </div>
      )}

      {uploading && (
        <p className="mt-2 text-xs text-emerald-600 font-medium" data-testid="dv-photo-uploading">Uploading…</p>
      )}
      {error && (
        <p className="mt-2 text-xs text-red-600 font-medium" data-testid="dv-photo-error">{error}</p>
      )}
      {photos.length === 0 && !uploading && (
        <p className="mt-3 text-[11px] text-gray-500">
          At least one photo is required to mark this DV complete.
        </p>
      )}
    </div>
  );
};
